// SupportChatWidget.jsx
// Conversational FAQ widget (Myntra-style): every bot message and its
// option buttons stay in a scrolling transcript, rather than replacing
// the screen at each step. Selecting an option appends a user bubble,
// then the next bot bubble (with its own buttons) appends below it.
//
// Requires: SupportChatWidget.css (same folder)
// Talks to: GET  /support/categories
//           GET  /support/categories/:categoryId/questions
//           GET  /support/questions/:questionId
//           POST /support/questions/:questionId/feedback
//           POST /support/escalate   (requires auth)
// via the shared `api` instance (src/services/api.js), which already
// prefixes REACT_APP_API_URL and attaches the auth token.

import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext'; // adjust path to match where this file lives relative to src/context/AuthContext.jsx
import {
  getSupportCategories,
  getSupportQuestions,
  getSupportQuestion,
  sendQuestionFeedback,
  escalateToTicket,
} from '../services/Supportservice'; // adjust path to match where this file lives relative to src/services/supportService.js
import './SupportChatWidget.css';

let idCounter = 0;
const nextId = () => `msg-${++idCounter}`;

export default function SupportChatWidget() {
  const { isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [transcript, setTranscript] = useState([]); // { id, from: 'bot'|'user', text, buttons?, buttonsDisabled? }
  const [isLoading, setIsLoading] = useState(false);
  const [activeQuestion, setActiveQuestion] = useState(null);
  const [inputMode, setInputMode] = useState(null); // null | 'ticket'
  const [inputValue, setInputValue] = useState('');
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcript, isOpen]);

  useEffect(() => {
    if (isOpen && transcript.length === 0) {
      startConversation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  function addBotMessage(text, buttons) {
    setTranscript((prev) => [...prev, { id: nextId(), from: 'bot', text, buttons }]);
  }

  function addUserMessage(text) {
    setTranscript((prev) => [...prev, { id: nextId(), from: 'user', text }]);
  }

  function disableButtons(messageId) {
    setTranscript((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, buttonsDisabled: true } : m))
    );
  }

  function handleOptionClick(messageId, label, action) {
    disableButtons(messageId);
    addUserMessage(label);
    action();
  }

  async function startConversation() {
    addBotMessage("Hi! I'm here to help. What do you need assistance with?");
    setIsLoading(true);
    try {
      const data = await getSupportCategories();
      const categories = data.categories || [];
      const msgId = nextId();
      setTranscript((prev) => [
        ...prev,
        {
          id: msgId,
          from: 'bot',
          text: 'Choose a topic:',
          buttons: categories.map((cat) => ({
            label: cat.name,
            onClick: () => handleOptionClick(msgId, cat.name, () => openCategory(cat)),
          })),
        },
      ]);
    } catch {
      addBotMessage("Sorry, I couldn't load help topics right now. Please try again shortly.");
    } finally {
      setIsLoading(false);
    }
  }

  async function openCategory(category) {
    setIsLoading(true);
    try {
      const data = await getSupportQuestions(category.id);
      const questions = data.questions || [];
      const msgId = nextId();
      setTranscript((prev) => [
        ...prev,
        {
          id: msgId,
          from: 'bot',
          text: questions.length ? 'Select a question:' : "There aren't any questions listed under this topic yet.",
          buttons: [
            ...questions.map((q) => ({
              label: q.question,
              onClick: () => handleOptionClick(msgId, q.question, () => openQuestion(q)),
            })),
            {
              label: '← Back to topics',
              onClick: () => handleOptionClick(msgId, '← Back to topics', () => startConversation()),
            },
          ],
        },
      ]);
    } catch {
      addBotMessage('Sorry, something went wrong loading that topic.');
    } finally {
      setIsLoading(false);
    }
  }

  async function openQuestion(questionSummary) {
    setIsLoading(true);
    try {
      const data = await getSupportQuestion(questionSummary.id);
      const question = data.question;
      setActiveQuestion(question);
      addBotMessage(question.answer);

      const msgId = nextId();
      setTranscript((prev) => [
        ...prev,
        {
          id: msgId,
          from: 'bot',
          text: 'Was this helpful?',
          buttons: [
            {
              label: 'Yes, I am all set',
              onClick: () => handleOptionClick(msgId, 'Yes, I am all set', () => handleFeedback(true, question)),
            },
            {
              label: 'No, I need further assistance',
              onClick: () => handleOptionClick(msgId, 'No, I need further assistance', () => handleFeedback(false, question)),
            },
          ],
        },
      ]);
    } catch {
      addBotMessage('Sorry, something went wrong loading that answer.');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleFeedback(helpful, question) {
    sendQuestionFeedback(question.id, { helpful }).catch(() => {}); // non-critical

    if (helpful) {
      const msgId = nextId();
      setTranscript((prev) => [
        ...prev,
        {
          id: msgId,
          from: 'bot',
          text: 'Great — glad that helped! Anything else?',
          buttons: [
            {
              label: 'Browse topics again',
              onClick: () => handleOptionClick(msgId, 'Browse topics again', () => startConversation()),
            },
            {
              label: 'End chat',
              onClick: () => handleOptionClick(msgId, 'End chat', () => endChat()),
            },
          ],
        },
      ]);
      return;
    }

    addBotMessage("I'm sorry that didn't fully answer your question.");

    const msgId = nextId();
    const buttons = [];

    if (isAuthenticated && question.allowLiveSupport) {
      buttons.push({
        label: 'Open a Support Ticket',
        onClick: () => handleOptionClick(msgId, 'Open a Support Ticket', () => beginTicket()),
      });
    }
    buttons.push({
      label: 'Browse topics again',
      onClick: () => handleOptionClick(msgId, 'Browse topics again', () => startConversation()),
    });

    let contactLine = '';
    if (!isAuthenticated) {
      contactLine = 'Please log in to open a support ticket.';
    }
    if (question.contactEmail || question.contactPhone) {
      contactLine += `${contactLine ? ' ' : ''}You can also reach us directly${
        question.contactEmail ? ` at ${question.contactEmail}` : ''
      }${question.contactPhone ? ` or ${question.contactPhone}` : ''}.`;
    }

    setTranscript((prev) => [
      ...prev,
      {
        id: msgId,
        from: 'bot',
        text: contactLine || 'What would you like to do next?',
        buttons,
      },
    ]);
  }

  function beginTicket() {
    addBotMessage('Please describe your issue and our team will follow up with you.');
    setInputMode('ticket');
  }

  async function submitTicket(e) {
    e.preventDefault();
    const trimmed = inputValue.trim();
    if (!trimmed) return;

    addUserMessage(trimmed);
    setInputValue('');
    setInputMode(null);
    setIsLoading(true);

    try {
      await escalateToTicket({
        questionId: activeQuestion?.id,
        message: trimmed,
      });

      addBotMessage('Your ticket has been created. Our team will follow up with you shortly.');
    } catch (err) {
      if (err.response?.status === 401) {
        addBotMessage('Please log in to open a support ticket.');
      } else {
        addBotMessage('Sorry, something went wrong creating your ticket. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }

  function endChat() {
    addBotMessage('Thanks for chatting with us! Reopen this any time you need help.');
  }

  return (
    <div className="support-widget-root">
      {isOpen && (
        <div className="support-widget-panel">
          <div className="support-widget-header">
            <div className="support-widget-header-info">
              <div className="support-widget-avatar">🤖</div>
              <div>
                <div className="support-widget-title">ALG DataGuard</div>
                <div className="support-widget-subtitle">Support</div>
              </div>
            </div>
            <button className="support-widget-close" onClick={() => setIsOpen(false)} aria-label="Close">
              ×
            </button>
          </div>

          <div className="support-widget-body">
            {transcript.map((msg) => (
              <div key={msg.id} className={`support-widget-row support-widget-row--${msg.from}`}>
                {msg.from === 'bot' && <div className="support-widget-avatar-sm">🤖</div>}
                <div className="support-widget-bubble-group">
                  <div className={`support-widget-bubble support-widget-bubble--${msg.from}`}>
                    {msg.text}
                  </div>
                  {msg.buttons && msg.buttons.length > 0 && (
                    <div className="support-widget-options">
                      {msg.buttons.map((btn, i) => (
                        <button
                          key={i}
                          className="support-widget-option-btn"
                          disabled={msg.buttonsDisabled}
                          onClick={btn.onClick}
                        >
                          {btn.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="support-widget-row support-widget-row--bot">
                <div className="support-widget-avatar-sm">🤖</div>
                <div className="support-widget-bubble support-widget-bubble--bot support-widget-typing">
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            )}

            <div ref={bottomRef} />
          </div>

          {inputMode === 'ticket' && (
            <form className="support-widget-input-row" onSubmit={submitTicket}>
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Describe your issue..."
                className="support-widget-input"
                autoFocus
              />
              <button type="submit" className="support-widget-send" disabled={!inputValue.trim()}>
                →
              </button>
            </form>
          )}
        </div>
      )}

      <button
        className="support-widget-toggle"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label={isOpen ? 'Close help' : 'Open help'}
      >
        {isOpen ? '×' : '?'}
      </button>
    </div>
  );
}